import type {
  DevexCodexFeedbackPullRequestState,
  DevexCodexFeedbackReactionFilter,
  DevexReviewLatencyGroupBy,
  DevexReviewLatencySizeBucket,
} from '@dev-console/types';
import {
  DEVEX_REVIEW_LATENCY_GROUP_BYS,
  DEVEX_REVIEW_LATENCY_SIZE_BUCKETS,
  DEVEX_REVIEW_LATENCY_TEAMS,
} from '@shared/dev-console/devex-metrics';

import { CodexFeedbackView } from '@/components/devex-metrics/codex-feedback-view';
import {
  GROUP_BY_LABELS,
  GROUP_VALUE_LABELS,
  SIZE_BUCKET_LABELS,
} from '@/components/devex-metrics/review-latency-labels';
import { ReviewLatencyView } from '@/components/devex-metrics/review-latency-view';
import { MultiSelect } from '@/components/ui/multi-select';
import { Tabs } from '@/components/ui/tabs';
import { useSearchParamsState } from '@/hooks/useSearchParamsState';

export const meta = {
  label: 'DevEx Metrics',
  order: 40,
};

const VALID_DAYS = [7, 30, 60, 90] as const;
const CODEX_FEEDBACK_STATES: DevexCodexFeedbackPullRequestState[] = ['OPEN', 'MERGED', 'CLOSED'];
const CODEX_FEEDBACK_STATE_LABELS: Record<DevexCodexFeedbackPullRequestState, string> = {
  CLOSED: 'Closed',
  MERGED: 'Merged',
  OPEN: 'Open',
};

type DevexMetricsView = 'latency' | 'codex-feedback';

const paramDefs = {
  days: { default: 90 },
  codexBotLogins: { default: '' },
  codexIncludeBotReplies: { default: false },
  codexPrNumbers: { default: '' },
  codexReactionFilter: {
    default: 'all' as DevexCodexFeedbackReactionFilter,
    parse: (raw: string): DevexCodexFeedbackReactionFilter => (raw === 'negative' ? 'negative' : 'all'),
  },
  codexStates: {
    default: CODEX_FEEDBACK_STATES,
    parse: (raw: string): DevexCodexFeedbackPullRequestState[] => {
      const states = raw
        ? raw
            .split(',')
            .filter((state): state is DevexCodexFeedbackPullRequestState =>
              CODEX_FEEDBACK_STATES.includes(state as DevexCodexFeedbackPullRequestState)
            )
        : CODEX_FEEDBACK_STATES;
      return states.length > 0 ? states : CODEX_FEEDBACK_STATES;
    },
  },
  excludeBots: { default: true },
  groupBy: {
    default: 'team' as DevexReviewLatencyGroupBy,
    parse: (raw: string): DevexReviewLatencyGroupBy =>
      DEVEX_REVIEW_LATENCY_GROUP_BYS.includes(raw as DevexReviewLatencyGroupBy)
        ? (raw as DevexReviewLatencyGroupBy)
        : 'team',
  },
  selectedGroup: { default: '' },
  sizeBuckets: {
    default: [] as DevexReviewLatencySizeBucket[],
    parse: (raw: string): DevexReviewLatencySizeBucket[] =>
      raw
        ? raw
            .split(',')
            .filter((bucket): bucket is DevexReviewLatencySizeBucket =>
              DEVEX_REVIEW_LATENCY_SIZE_BUCKETS.includes(bucket as DevexReviewLatencySizeBucket)
            )
        : [],
  },
  teams: { default: [] as string[] },
  view: {
    default: 'latency' as DevexMetricsView,
    parse: (raw: string): DevexMetricsView => (raw === 'codex-feedback' ? 'codex-feedback' : 'latency'),
  },
};

export function DevexMetricsPage() {
  const {
    codexBotLogins: [codexBotLogins, setCodexBotLogins],
    codexIncludeBotReplies: [codexIncludeBotReplies, setCodexIncludeBotReplies],
    codexPrNumbers: [codexPrNumbers, setCodexPrNumbers],
    codexReactionFilter: [codexReactionFilter, setCodexReactionFilter],
    codexStates: [codexStates, setCodexStates],
    days: [days, setDays],
    excludeBots: [excludeBots, setExcludeBots],
    groupBy: [groupBy, setGroupBy],
    selectedGroup: [selectedGroup, setSelectedGroup],
    sizeBuckets: [sizeBuckets, setSizeBuckets],
    teams: [teams, setTeams],
    view: [view, setView],
  } = useSearchParamsState(paramDefs);

  const dayControls = (
    <>
      <span className='text-sm text-neutral-500'>Days</span>
      {VALID_DAYS.map((d) => (
        <button
          key={d}
          onClick={() => setDays(d)}
          className={`rounded px-3 py-1 text-sm transition-colors ${
            days === d
              ? 'bg-neutral-900 text-white'
              : 'border border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400'
          }`}
        >
          {d}d
        </button>
      ))}
    </>
  );

  return (
    <div>
      <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
        <h2 className='font-mediumbold text-xl text-neutral-900'>Devex Metrics</h2>
      </div>

      <Tabs
        className='mb-4'
        activeTab={view}
        onTabChange={(tabId) => setView(tabId as DevexMetricsView)}
        tabs={[
          { id: 'latency', label: 'Review Latency' },
          { id: 'codex-feedback', label: 'Codex Feedback' },
        ]}
      />

      {view === 'latency' ? (
        <>
          <div className='mb-4 flex flex-wrap items-center gap-2'>
            {dayControls}
            <label className='flex min-h-9 min-w-[150px] items-center gap-2 rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-sm text-neutral-700 hover:border-neutral-300'>
              <span className='text-neutral-500'>Group by</span>
              <select
                value={groupBy}
                onChange={(event) => {
                  setGroupBy(event.target.value as DevexReviewLatencyGroupBy);
                  // selectedGroup keys are dimension-specific, so a stale one would linger in the URL.
                  setSelectedGroup('');
                }}
                className='font-mediumbold min-w-[92px] bg-transparent text-neutral-900 focus:outline-none'
              >
                {DEVEX_REVIEW_LATENCY_GROUP_BYS.map((value) => (
                  <option key={value} value={value}>
                    {GROUP_BY_LABELS[value]}
                  </option>
                ))}
              </select>
            </label>
            <MultiSelect
              emptyLabel='All teams'
              label='Teams'
              options={DEVEX_REVIEW_LATENCY_TEAMS}
              formatOption={(option) => GROUP_VALUE_LABELS.team[option] ?? option}
              selected={teams}
              onChange={setTeams}
            />
            <MultiSelect
              emptyLabel='All sizes'
              label='Size'
              options={DEVEX_REVIEW_LATENCY_SIZE_BUCKETS}
              selected={sizeBuckets}
              formatOption={(option) => SIZE_BUCKET_LABELS[option as DevexReviewLatencySizeBucket] ?? option}
              onChange={(next) => setSizeBuckets(next as DevexReviewLatencySizeBucket[])}
            />
            <label className='flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-sm text-neutral-700'>
              <input
                type='checkbox'
                checked={excludeBots}
                onChange={(event) => setExcludeBots(event.target.checked)}
                className='rounded border-neutral-300'
              />
              Exclude bots
            </label>
          </div>
          <ReviewLatencyView
            days={days}
            excludeBots={excludeBots}
            groupBy={groupBy}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            sizeBuckets={sizeBuckets}
            teamFilters={teams}
          />
        </>
      ) : (
        <div className='flex flex-wrap items-center gap-2'>
          {dayControls}
          <MultiSelect
            emptyLabel='All states'
            label='State'
            options={CODEX_FEEDBACK_STATES}
            selected={codexStates}
            formatOption={(option) =>
              CODEX_FEEDBACK_STATE_LABELS[option as DevexCodexFeedbackPullRequestState] ?? option
            }
            onChange={(next) => setCodexStates(next as DevexCodexFeedbackPullRequestState[])}
          />
          <label className='flex min-h-9 min-w-[210px] items-center gap-2 rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-sm text-neutral-700'>
            <span className='shrink-0 text-neutral-500'>PRs</span>
            <input
              value={codexPrNumbers}
              onChange={(event) => setCodexPrNumbers(event.target.value)}
              placeholder='123, 456'
              className='min-w-0 flex-1 bg-transparent text-neutral-900 placeholder:text-neutral-400 focus:outline-none'
            />
          </label>
          <label className='flex min-h-9 min-w-[260px] items-center gap-2 rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-sm text-neutral-700'>
            <span className='shrink-0 text-neutral-500'>Bot logins</span>
            <input
              value={codexBotLogins}
              onChange={(event) => setCodexBotLogins(event.target.value)}
              placeholder='chatgpt-codex-connector[bot]'
              className='min-w-0 flex-1 bg-transparent text-neutral-900 placeholder:text-neutral-400 focus:outline-none'
            />
          </label>
          <label className='flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-sm text-neutral-700'>
            <input
              type='checkbox'
              checked={codexIncludeBotReplies}
              onChange={(event) => setCodexIncludeBotReplies(event.target.checked)}
              className='rounded border-neutral-300'
            />
            Include bot replies
          </label>
          <label className='flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-sm text-neutral-700'>
            <input
              type='checkbox'
              checked={codexReactionFilter === 'negative'}
              onChange={(event) => setCodexReactionFilter(event.target.checked ? 'negative' : 'all')}
              className='rounded border-neutral-300'
            />
            Negative feedback only
          </label>
        </div>
      )}

      {view === 'codex-feedback' && (
        <div className='mt-4'>
          <CodexFeedbackView
            botLogins={codexBotLogins}
            days={days}
            includeBotReplies={codexIncludeBotReplies}
            prNumbers={codexPrNumbers}
            reactionFilter={codexReactionFilter}
            states={codexStates}
          />
        </div>
      )}
    </div>
  );
}
